// PWA utilities and service worker registration

export const isPWAInstalled = () => {
  // Check display-mode (works when running as installed PWA)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // Check iOS Safari standalone mode
  const isIOSStandalone = window.navigator.standalone === true;
  // Check if we previously recorded an installation (fallback)
  const wasInstalled = localStorage.getItem('pwa_installed') === 'true';
  
  return isStandalone || isIOSStandalone || wasInstalled;
};

export const canInstallPWA = () => {
  return 'BeforeInstallPromptEvent' in window || 
         (!isPWAInstalled() && 'serviceWorker' in navigator);
};

let deferredPrompt = null;

export const initPWA = () => {
  // Listen for the beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    // Dispatch custom event for UI to show install button
    window.dispatchEvent(new CustomEvent('pwa-installable'));
  });

  // Listen for successful installation
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });

  // Register service worker
  registerServiceWorker();
};

export const promptInstall = async () => {
  if (!deferredPrompt) {
    return { outcome: 'unavailable' };
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return { outcome };
};

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for SW updates
      });

      // Check for updates immediately on registration
      registration.update();

      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available
            window.dispatchEvent(new CustomEvent('pwa-update-available'));
          }
        });
      });

      return registration;
    } catch (_e) {
      // Service worker registration failed - PWA features unavailable
      return null;
    }
  }
  return null;
};

// Force check for updates
export const checkForUpdates = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    return true;
  }
  return false;
};

// Skip waiting and activate new service worker
export const activateUpdate = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }
};

// =========================================
// PUSH NOTIFICATION SUBSCRIPTION
// =========================================

// Convert VAPID public key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Delays (ms) before each registration attempt with the backend. The first
// attempt is immediate; the retries cover a transient network failure or a
// token that has just been refreshed. Without a retry, a single rejected
// request leaves the device without push for the whole session.
const REGISTER_RETRY_DELAYS_MS = [0, 3000, 10000];

// How long to wait for an active service worker registration. `ready` never
// rejects, and never settles when no registration exists.
const SW_READY_TIMEOUT_MS = 3000;

// How long to wait for the backend's VAPID public key.
const VAPID_KEY_TIMEOUT_MS = 5000;

const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(() => resolve(null), ms)),
]);

// The VAPID public key, resolved once per page load when it succeeds.
//
// The key is fetched from the backend (GET /api/push/vapid-public-key) rather
// than read from the build-time REACT_APP_VAPID_PUBLIC_KEY, because the
// build-time value is only present in the bundle if the build pipeline
// happened to pass it through. It did not: frontend/Dockerfile declared no
// ARG for it, so CRA inlined `undefined` and subscribeToPush() bailed out
// before ever creating a subscription — which is why Android users received
// nothing when the app was not in the foreground (KAN-132).
//
// Fetching from the backend also guarantees the key matches the private key
// the backend signs with, so the two can never drift apart.
let vapidKeyPromise = null;

export const getVapidPublicKey = async () => {
  if (vapidKeyPromise) return vapidKeyPromise;

  vapidKeyPromise = (async () => {
    const buildTimeKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await withTimeout(
        fetch(`${apiUrl}/api/push/vapid-public-key`),
        VAPID_KEY_TIMEOUT_MS
      );
      if (response && response.ok) {
        const data = await response.json();
        if (data && data.public_key) {
          console.log('[push] using the VAPID public key returned by the backend');
          return data.public_key;
        }
        console.warn('[push] the backend returned an empty VAPID public key');
      } else {
        console.warn('[push] could not fetch the VAPID public key from the backend');
      }
    } catch (error) {
      console.warn('[push] could not fetch the VAPID public key from the backend:', error);
    }

    // Fall back to the build-time value when the backend cannot be reached.
    if (buildTimeKey) {
      console.log('[push] falling back to the build-time VAPID public key');
      return buildTimeKey;
    }
    return null;
  })();

  // A failure is NOT cached: a transient backend outage at page load would
  // otherwise disable push for the whole session, because every later
  // subscribeToPush() would reuse the null result without ever retrying the
  // fetch. A resolved key is cached (KAN-132).
  const key = await vapidKeyPromise;
  if (!key) vapidKeyPromise = null;
  return key;
};

// Register an existing push subscription with the backend.
// Returns true on success, false on any failure (logged, never thrown).
// Exported so the single-attempt behaviour can be tested without the retry
// backoff, which is not timer-friendly under Jest 27.
export const registerPushSubscription = async (subscription, token) => {
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${apiUrl}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription.toJSON())
    });

    // The response has to be inspected: a rejected request (e.g. 401 from an
    // expired token) means the backend never stored the subscription, so it
    // will never push to this device. Treating that as success is what made
    // this failure invisible (KAN-132).
    if (!response.ok) {
      console.warn(
        `[push] subscription registration rejected: ${response.status} ${response.statusText}`
      );
      return false;
    }

    console.log('[push] subscription registered with the backend');
    return true;
  } catch (error) {
    console.warn('[push] subscription registration request failed:', error);
    return false;
  }
};

/**
 * Request notification permission and register the push subscription.
 *
 * The subscription is registered as part of this function rather than by the
 * caller so that nothing cosmetic can abort it. `new Notification()` throws a
 * TypeError in nearly all mobile browsers, and when that happened inside the
 * "Enable Notifications" click handler it aborted the handler *before*
 * `subscribeToPush()` ran — the permission was granted, but no push
 * subscription was ever created, so Android users never received a
 * notification while iOS users did (KAN-132).
 *
 * @returns {Promise<{permission: string, subscription: Promise<any>}>}
 *   `permission` is one of 'granted', 'denied', 'default', 'unsupported' or
 *   'error'. `subscription` resolves with the push subscription (or null) and
 *   never rejects.
 */
export const enablePushNotifications = async (token) => {
  if (!('Notification' in window)) {
    return { permission: 'unsupported', subscription: Promise.resolve(null) };
  }

  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (error) {
    console.warn('[push] Notification.requestPermission() failed:', error);
    return { permission: 'error', subscription: Promise.resolve(null) };
  }

  // Registering the subscription is the part that must happen. It is started
  // here (not awaited) so the UI can respond immediately, and the promise is
  // guaranteed not to reject.
  const subscription = permission === 'granted' && token
    ? subscribeToPush(token).catch((error) => {
      console.warn('[push] push subscription failed:', error);
      return null;
    })
    : Promise.resolve(null);

  return { permission, subscription };
};

// Subscribe to push notifications
export const subscribeToPush = async (token) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[push] this browser does not support push notifications');
    return null;
  }

  const vapidPublicKey = await getVapidPublicKey();
  if (!vapidPublicKey) {
    console.warn(
      '[push] no VAPID public key available — the backend must have VAPID_PUBLIC_KEY set, ' +
      'or REACT_APP_VAPID_PUBLIC_KEY must be present in the build. Cannot subscribe to push.'
    );
    return null;
  }

  let subscription;
  try {
    const registration = await withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS);
    if (!registration) {
      console.warn('[push] no active service worker registration — cannot subscribe to push');
      return null;
    }

    // Check if already subscribed
    subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Subscribe with VAPID key
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      console.log('[push] created a new push subscription');
    } else {
      // Re-register the existing subscription so the backend's copy is
      // refreshed (key rotation, backend redeploy, or a previous failed
      // registration).
      console.log('[push] re-registering the existing push subscription');
    }
  } catch (error) {
    console.warn('[push] pushManager.subscribe() failed:', error);
    return null;
  }

  for (const delay of REGISTER_RETRY_DELAYS_MS) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    // Re-read the token on every attempt: it may have been refreshed since the
    // call was made.
    const currentToken = localStorage.getItem('token') || token;
    if (!currentToken) {
      console.warn('[push] no auth token available — cannot register the push subscription');
      return null;
    }
    if (await registerPushSubscription(subscription, currentToken)) {
      return subscription;
    }
  }

  console.warn('[push] giving up on registering the push subscription after retries');
  return null;
};

// Check if push is subscribed
export const isPushSubscribed = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (_e) {
    return false;
  }
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return { permission: 'unsupported' };
  }

  if (Notification.permission === 'granted') {
    return { permission: 'granted' };
  }

  if (Notification.permission === 'denied') {
    return { permission: 'denied' };
  }

  const permission = await Notification.requestPermission();
  return { permission };
};

// Check if device is iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Check if device is Android
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

// Get platform-specific install instructions
export const getInstallInstructions = () => {
  if (isIOS()) {
    return {
      platform: 'iOS',
      steps: [
        'Tap the Share button (square with up-arrow) at the bottom of your screen',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add"'
      ]
    };
  } else if (isAndroid()) {
    return {
      platform: 'Android',
      steps: [
        'Tap the menu button (three dots) in Chrome',
        'Tap "Add to Home screen" or "Install app"',
        'Tap "Add" or "Install" to confirm',
        'ExpressSub will now appear on your home screen!'
      ]
    };
  } else {
    return {
      platform: 'Desktop',
      steps: [
        'Click the install icon in the address bar (or menu)',
        'Click "Install" to add ExpressSub to your applications',
        'You can now launch ExpressSub from your desktop or start menu'
      ]
    };
  }
};
